import { createTheme } from '@mui/material/styles'
import { PaletteMode } from '@mui/material'

// Extend MUI theme types to include entityColors and analyticsColors
declare module '@mui/material/styles' {
  interface Palette {
    entityColors: {
      ceActivity: string
      assignment: string
      userAction: string
      licenseActivity: string
      credentialActivity: string
      extra1: string
    }
    analyticsColors: {
      userMetrics: string
      ceHours: string
      activities: string
      recordedCourses: string
      liveClasses: string
      externalCerts: string
      assignedActivities: string
      selfPacedLearning: string
      events: string
    }
    contentPipelineColors: {
      planning: string
      compliance: string
      progress: string
      content: string
      workflow: string
      quality: string
    }
    accreditationColors: {
      capce: string
      ancc: string
      ceBroker: string
      virginia: string
      newJersey: string
      newYork: string
      accredited: string
      notAccredited: string
    }
  }

  interface PaletteOptions {
    entityColors?: {
      ceActivity?: string
      assignment?: string
      userAction?: string
      licenseActivity?: string
      credentialActivity?: string
      extra1?: string
    }
    analyticsColors?: {
      userMetrics?: string
      ceHours?: string
      activities?: string
      recordedCourses?: string
      liveClasses?: string
      externalCerts?: string
      assignedActivities?: string
      selfPacedLearning?: string
      events?: string
    }
    contentPipelineColors?: {
      planning?: string
      compliance?: string
      progress?: string
      content?: string
      workflow?: string
      quality?: string
    }
    accreditationColors?: {
      capce?: string
      ancc?: string
      ceBroker?: string
      virginia?: string
      newJersey?: string
      newYork?: string
      accredited?: string
      notAccredited?: string
    }
  }
}

export const paletteColorsDark = {
  primary: '#6694e9',
  primaryLight: '#8cb3ee',
  primaryDark: '#5073c8',
  textOnPrimary: '#000000',
  secondary: '#f9ea4f',
  secondaryLight: '#fcf17d',
  secondaryDark: '#ecbf3f',
  textOnSecondary: '#000000',
  error: '#cf6679',
  background: {
    default: '#000000',
    menu: '#0F0F0F',
    paper: '#1e2124',
    paperDark: '#121212',
  },
  text: '#bbe1fa',
  entityColors: {
    ceActivity: '#02B2AF', // Teal - all CE activities (blueberryTwilight color 1)
    assignment: '#72CCFF', // Light blue - assignments (blueberryTwilight color 2)
    userAction: '#FFE082', // Light orange-yellow - user actions (blueberryTwilight color 3)
    licenseActivity: '#B39DDB', // Light Purple - license activities (blueberryTwilight color 4)
    credentialActivity: '#2E96FF', // Blue - credential activities (blueberryTwilight color 5)
    extra1: '#e6ee9c', // Light green-yellow - future entity type (blueberryTwilight color 6)
  },
  analyticsColors: {
    userMetrics: '#02B2AF', // Teal - Active Users, Total Users (blueberryTwilight color 1)
    ceHours: '#72CCFF', // Light blue - Total CE Hours, Avg Hours/User (blueberryTwilight color 2)
    activities: '#FFE082', // Light orange-yellow - Total Activities (blueberryTwilight color 3)
    recordedCourses: '#B39DDB', // Light Purple - Recorded Courses activity type (blueberryTwilight color 4)
    liveClasses: '#2E96FF', // Blue - Live Classes activity type (blueberryTwilight color 5)
    externalCerts: '#e6ee9c', // Light green-yellow - External Certificates activity type (blueberryTwilight color 6)
    assignedActivities: '#B39DDB', // Light Purple - Assigned activities (blueberryTwilight color 4)
    selfPacedLearning: '#2E96FF', // Blue - Self-paced learning (blueberryTwilight color 5)
    events: '#e6ee9c', // Light green-yellow - Events activity type (blueberryTwilight color 6)
  },
  contentPipelineColors: {
    planning: '#02B2AF', // Teal - planning/creation activities (blueberryTwilight color 1)
    compliance: '#72CCFF', // Light blue - compliance/review activities (blueberryTwilight color 2)
    progress: '#FFE082', // Light orange-yellow - progress metrics (blueberryTwilight color 3)
    content: '#B39DDB', // Light Purple - content-related stats (blueberryTwilight color 4)
    workflow: '#2E96FF', // Blue - workflow/process items (blueberryTwilight color 5)
    quality: '#e6ee9c', // Light green-yellow - quality/approval metrics (blueberryTwilight color 6)
  },
  accreditationColors: {
    capce: '#02B2AF', // Teal - CAPCE
    ancc: '#72CCFF', // Light blue - ANCC
    ceBroker: '#B39DDB', // Light Purple - CE Broker (per-state)
    virginia: '#FFE082', // Light orange-yellow - Virginia
    newJersey: '#2E96FF', // Blue - New Jersey
    newYork: '#e6ee9c', // Light green-yellow - New York
    accredited: '#81C784', // Green - generic accredited
    notAccredited: '#9e9e9e', // Grey - generic not accredited
  },
}

export const paletteColorsLight = {
  primary: '#212121',
  primaryLight: '#484848',
  primaryDark: '#000000',
  textOnPrimary: '#ffffff',
  secondary: '#42a5f5',
  secondaryLight: '#80d6ff',
  secondaryDark: '#0077c2',
  textOnSecondary: '#000000',
  error: '#E44C65',
  background: {
    default: '#00030f',
    paper: '#303030',
  },
  text: '#050505',
  entityColors: {
    ceActivity: '#02B2AF', // Teal - all CE activities (blueberryTwilight color 1)
    assignment: '#72CCFF', // Light blue - assignments (blueberryTwilight color 2)
    userAction: '#DA00FF', // Magenta - user actions (blueberryTwilight color 3)
    licenseActivity: '#9001CB', // Purple - license activities (blueberryTwilight color 4)
    credentialActivity: '#2E96FF', // Blue - credential activities (blueberryTwilight color 5)
    extra1: '#3B48E0', // Dark blue - future entity type (blueberryTwilight color 6)
  },
  analyticsColors: {
    userMetrics: '#02B2AF', // Teal - Active Users, Total Users (blueberryTwilight color 1)
    ceHours: '#72CCFF', // Light blue - Total CE Hours, Avg Hours/User (blueberryTwilight color 2)
    activities: '#DA00FF', // Magenta - Total Activities (blueberryTwilight color 3)
    recordedCourses: '#9001CB', // Purple - Recorded Courses activity type (blueberryTwilight color 4)
    liveClasses: '#2E96FF', // Blue - Live Classes activity type (blueberryTwilight color 5)
    externalCerts: '#3B48E0', // Dark blue - External Certificates activity type (blueberryTwilight color 6)
    assignedActivities: '#9001CB', // Purple - Assigned activities (blueberryTwilight color 4)
    selfPacedLearning: '#2E96FF', // Blue - Self-paced learning (blueberryTwilight color 5)
    events: '#3B48E0', // Dark blue - Events activity type (blueberryTwilight color 6)
  },
  contentPipelineColors: {
    planning: '#02B2AF', // Teal - planning/creation activities (blueberryTwilight color 1)
    compliance: '#72CCFF', // Light blue - compliance/review activities (blueberryTwilight color 2)
    progress: '#DA00FF', // Magenta - progress metrics (blueberryTwilight color 3)
    content: '#9001CB', // Purple - content-related stats (blueberryTwilight color 4)
    workflow: '#2E96FF', // Blue - workflow/process items (blueberryTwilight color 5)
    quality: '#3B48E0', // Dark blue - quality/approval metrics (blueberryTwilight color 6)
  },
  accreditationColors: {
    capce: '#02B2AF', // Teal - CAPCE
    ancc: '#72CCFF', // Light blue - ANCC
    ceBroker: '#9001CB', // Purple - CE Broker (per-state)
    virginia: '#DA00FF', // Magenta - Virginia
    newJersey: '#2E96FF', // Blue - New Jersey
    newYork: '#3B48E0', // Dark blue - New York
    accredited: '#2e7d32', // Green - generic accredited
    notAccredited: '#616161', // Grey - generic not accredited
  },
}

const typographyTheme = {
  h1: {
    fontSize: 56,
    fontWeight: 700,
    lineHeight: '112px',
    letterSpacing: '-1.5px',
    /* , fontFamily: 'smileAndWave' */
  },
  h2: {
    fontSize: 46,
    fontWeight: 600,
    lineHeight: '72px',
    letterSpacing: '-0.5px',
    /* , fontFamily: 'smileAndWave' */
  },
  h3: {
    fontSize: 38,
    fontWeight: 500,
    lineHeight: '56px',
    letterSpacing: '0px',
    /* , fontFamily: 'smileAndWave' */
  },
  h4: {
    fontSize: 30,
    fontWeight: 400,
    lineHeight: '36px',
    letterSpacing: '0px',
  },
  h5: {
    fontSize: 24,
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.18px',
  },
  h6: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  subtitle1: {
    fontSize: 16,
    // fontWeight: 400,
    // lineHeight: '24px',
    // letterSpacing: '0.15px',
  },
  subtitle2: {
    fontSize: 14,
    // fontWeight: 500,
    // lineHeight: '24px',
    // letterSpacing: '0.1px',
  },
  body1: {
    fontSize: 16,
    // fontWeight: 400,
    // lineHeight: '24px',
    // letterSpacing: '0.5px',
  },
  body2: {
    fontSize: 14,
    // fontWeight: 400,
    // lineHeight: '20px',
    // letterSpacing: '0.25px',
  },
  caption: {
    fontSize: 12,
  },
  overline: {
    fontSize: 12,
  },
  button: {
    textTransform: 'none',
  },
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  fontFamily: [
    'Roboto',
    'Helvetica',
    'Arial',
    'sans-serif',
    'smileAndWave',
  ].join(','),
}

const options = (dark: boolean) => {
  let paletteColors = dark ? paletteColorsDark : paletteColorsLight
  return {
    palette: {
      mode: dark ? 'dark' : ('light' as PaletteMode),
      primary: {
        main: paletteColors.primary,
        light: paletteColors.primaryLight,
        dark: paletteColors.primaryDark,
        contrastText: paletteColors.textOnPrimary,
      },
      secondary: {
        main: paletteColors.secondary,
        light: paletteColors.secondaryLight,
        dark: paletteColors.secondaryDark,
        contrastText: paletteColors.textOnSecondary,
      },
      error: {
        main: paletteColors.error,
      },
      background: paletteColors.background,
      entityColors: paletteColors.entityColors,
      analyticsColors: paletteColors.analyticsColors,
      contentPipelineColors: paletteColors.contentPipelineColors,
      accreditationColors: paletteColors.accreditationColors,
    },
    typography: typographyTheme,
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'smileAndWave';
            font-style: 'normal';
            font-weight: 400;
            src: url("/fonts/smileandwave.ttf");
          }
          html, body, #root {
            height: 100%;
          }
      `,
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: '2px solid #ffffff',
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: '2px solid #ffffff',
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: dark
              ? paletteColorsDark.background.default
              : paletteColorsLight.background.default,
          },
        },
      },
    },
  }
}
export const darkTheme = createTheme(options(true))
export const lightTheme = createTheme(options(false))

export default darkTheme
